/**
 * Centrale axios-instance voor de SPA. Onthoudt:
 *
 *  - `withCredentials: true` zodat Laravel-Sanctum's session-cookie
 *    meelift bij elke request (zelfde host, geen CORS).
 *  - `X-Requested-With: XMLHttpRequest` markeert het als AJAX zodat
 *    Laravel CSRF-handling z'n redirect-naar-login overslaat.
 *  - `installAuthErrorHandler` plugt een 401-interceptor in die de
 *    gebruiker naar het login-scherm stuurt zodra de sessie verloopt.
 *    De redirect-target is mode-afhankelijk (superadmin vs tenant) en
 *    wordt eenmaal bij app-boot meegegeven.
 *
 * `ensureCsrf()` haalt het CSRF-cookie op vóór een POST/PUT/PATCH op
 * routes die door Sanctum's `EnsureFrontendRequestsAreStateful` lopen.
 * Vue-versie deed hetzelfde.
 */
import axios, { type AxiosInstance } from 'axios';

import { i18n } from '@shared/i18n';

export const api: AxiosInstance = axios.create({
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Accept': 'application/json',
  },
});

// Stuur de actieve UI-taal mee zodat de server (SetRequestLocale) z'n
// validatie-/foutmeldingen in diezelfde taal teruggeeft. `Accept-Language` kan
// JS niet zetten (verboden header) — die reflecteert alleen de browsertaal —
// dus gebruiken we een custom `X-Locale`-header die de taalschakelaar volgt.
api.interceptors.request.use((config) => {
  config.headers.set('X-Locale', i18n.language || 'nl');
  return config;
});

/**
 * Alleen in ontwikkeling: een geweigerd verzoek (422) in de console, met wat de server
 * afkeurde. Een formulier toont vaak maar één melding, of geen als het veld niet op het
 * scherm staat; hier zie je de hele `errors`-lijst zonder het netwerkpaneel te openen. De
 * server houdt hetzelfde bij in zijn eigen log (`LOG_VALIDATION`, zie docker/README.md).
 *
 * `isDev` is een parameter zodat de test beide kanten kan meten; in de app is het altijd
 * `import.meta.env.DEV`, en een productie-build registreert de interceptor niet eens.
 */
export function warnOnRejectedRequest(error: unknown, isDev: boolean = import.meta.env.DEV): void {
  if (!isDev || !axios.isAxiosError(error) || error.response?.status !== 422) return;

  const method = (error.config?.method ?? 'get').toUpperCase();
  const data: unknown = error.response.data;
  const errors = typeof data === 'object' && data !== null && 'errors' in data ? data.errors : data;
  console.warn(`[422] ${method} ${error.config?.url ?? ''}`, errors);
}

if (import.meta.env.DEV) {
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      warnOnRejectedRequest(error);
      return Promise.reject(error);
    },
  );
}

/** Haal het XSRF-cookie op voor mutaties op stateful routes. */
export async function ensureCsrf(): Promise<void> {
  await api.get('/sanctum/csrf-cookie');
}

let authErrorRedirect: string | null = null;
let authErrorNavigate: ((path: string) => void) | null = null;
let authErrorClear: (() => void) | null = null;

/**
 * Roept bij app-boot. De navigate-callback (later gezet door <App/>) gebruikt
 * `react-router`'s navigate i.p.v. `window.location` zodat we de SPA-staat
 * behouden. `onAuthError` wist de lokale auth-staat (Zustand) zodat de
 * login-guard geen verouderde user meer ziet — zonder dat zou `GuestOnly` op
 * /login terugstuiteren naar het dashboard (de "catalogus stuitert terug"-bug).
 */
export function installAuthErrorHandler(redirectPath: string, onAuthError?: () => void): void {
  authErrorRedirect = redirectPath;
  authErrorClear = onAuthError ?? null;

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        // Skip de auto-redirect voor /auth/callback. Die endpoint geeft
        // 401 als het dev-login-token al verbruikt is — wat juist op
        // het gelukkige pad gebeurt door StrictMode's dubbele mount in
        // LoginView. Een redirect naar /login zou de net-aangemaakte
        // tenant-sessie ongedaan maken (ADR-0022 §"Login-callback race").
        const requestUrl: string = error?.config?.url ?? '';
        if (requestUrl.includes('/auth/callback')) {
          return Promise.reject(error);
        }
        // Een 401 = de server-sessie is weg. Wis eerst synchroon de lokale
        // auth-staat, dan pas navigeren — anders ziet de guard op de doelroute
        // nog de oude user. Wissen doen we altijd (ook al staan we al op
        // /login), zodat een verouderde user daar niet blijft hangen.
        authErrorClear?.();

        // ⚠️ MAAR NIET NAVIGEREN OP "WIE BEN IK". Een 401 op `/auth/me` is geen
        // sessie die wegviel maar het normale antwoord voor iemand die niet is
        // ingelogd — en de app stelt die vraag bij élke boot, ook op een pagina
        // die juist zonder inloggen bedoeld is. Gevolg vóór deze regel: de
        // publieke speler (ADR-0033 § 5) laadde, vroeg wie je bent, kreeg 401,
        // en stuurde de bezoeker naar het inlogscherm — precies wat "zonder
        // inloggen te openen" niet is. Gevonden door de browser-rooktest.
        //
        // Het wissen hierboven blijft wél gebeuren, en dat is genoeg: staat de
        // bezoeker op een beschermde route, dan ziet `RequireAuth` een lege user
        // en stuurt hém door. De guard weet welke routes bescherming nodig
        // hebben; een interceptor in de HTTP-laag weet dat niet.
        const isWhoAmI = requestUrl.includes('/auth/me');

        // Geen oneindige loop op de login-pagina zelf.
        if (! isWhoAmI && typeof window !== 'undefined' && window.location.pathname !== authErrorRedirect) {
          if (authErrorNavigate !== null) {
            authErrorNavigate(authErrorRedirect ?? '/login');
          } else {
            window.location.assign(authErrorRedirect ?? '/login');
          }
        }
      }
      return Promise.reject(error);
    },
  );
}

/** App-shell roept dit zodra de router gemount is. */
export function setAuthErrorNavigator(navigate: (path: string) => void): void {
  authErrorNavigate = navigate;
}
